// @ts-nocheck
import React from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';

import Avatar from '../Avatar/Avatar';
import { InlineTextButton } from '../Button/Button';
import Menu from '../Menu/Menu';
import MenuContent from '../MenuContent/MenuContent';
import MenuItem from '../MenuItem/MenuItem';
import MenuLabel from '../MenuLabel/MenuLabel';
import NamedLink from '../NamedLink/NamedLink';
import { usePersonaSwitcherMenuItems } from '../Persona/PersonaSwitcherMenu';
import { personaCopy } from '../../config/marketplaceContent';
import { useWalletSessionMaybe } from '../../context/WalletSessionContext';
import { isUserAuthorized } from '../../util/userHelpers';

import css from './MegaMenuSessionControls.module.css';

const MegaMenuWalletMenu = ({ isDark }) => {
  const walletSession = useWalletSessionMaybe();
  const personaMenuItems = usePersonaSwitcherMenuItems();

  if (!walletSession || walletSession.loading) {
    return null;
  }

  const {
    authMode,
    walletLabel,
    walletAddress,
    disconnectWalletSession,
  } = walletSession;

  if (authMode === 'web3') {
    return (
      <Menu skipFocusOnNavigation={true}>
        <MenuLabel className={css.walletPill} isOpenClassName={css.walletPillOpen} ariaLabel="Wallet session">
          <span className={css.walletDot} aria-hidden="true" />
          <span className={isDark ? css.walletText : css.walletTextLight}>{walletLabel}</span>
        </MenuLabel>
        <MenuContent className={css.menuContent}>
          <MenuItem key="wallet-hub">
            <Link to="/wallet" className={css.menuLink}>
              Manage wallet
            </Link>
          </MenuItem>
          <MenuItem key="profile-hub">
            <Link to="/settings/profile" className={css.menuLink}>
              {personaCopy.managePersonasLink}
            </Link>
          </MenuItem>
          {personaMenuItems}
          <MenuItem key="wallet-copy">
            <span className={css.menuMeta}>{walletAddress}</span>
          </MenuItem>
          <MenuItem key="wallet-disconnect">
            <InlineTextButton rootClassName={css.disconnectButton} onClick={disconnectWalletSession}>
              Disconnect wallet
            </InlineTextButton>
          </MenuItem>
        </MenuContent>
      </Menu>
    );
  }

  if (authMode === 'web2') {
    return null;
  }

  return (
    <Link
      to="/wallet"
      className={isDark ? css.connectLink : css.connectLinkLight}
      data-testid="mega-menu-connect-wallet"
    >
      Connect wallet
    </Link>
  );
};

const MegaMenuAccountMenu = ({ currentUser, onLogout }) => {
  const personaMenuItems = usePersonaSwitcherMenuItems();

  return (
    <Menu skipFocusOnNavigation={true}>
      <MenuLabel className={css.profileLabel} ariaLabel="Account menu">
        <Avatar className={css.avatar} user={currentUser} disableProfileLink />
      </MenuLabel>
      <MenuContent className={css.menuContent}>
        <MenuItem key="profile-hub">
          <Link to="/settings/profile" className={css.menuLink}>
            {personaCopy.managePersonasLink}
          </Link>
        </MenuItem>
        <MenuItem key="profile-settings">
          <NamedLink name="ProfileSettingsPage" className={css.menuLink}>
            Profile settings
          </NamedLink>
        </MenuItem>
        {personaMenuItems}
        <MenuItem key="account-settings">
          <NamedLink name="AccountSettingsPage" className={css.menuLink}>
            Account settings
          </NamedLink>
        </MenuItem>
        <MenuItem key="logout">
          <InlineTextButton rootClassName={css.disconnectButton} onClick={onLogout}>
            Log out
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};

const MegaMenuSessionControls = ({ onLogout, isDark = true }) => {
  const walletSession = useWalletSessionMaybe();
  const authMode = walletSession?.authMode || 'none';
  const isSharetribeAuthenticated = useSelector(state => Boolean(state.auth?.isAuthenticated));
  const currentUser = useSelector(state => state.user?.currentUser);
  const showWeb2Menu =
    authMode === 'web2' && isSharetribeAuthenticated && isUserAuthorized(currentUser);
  const showWeb2AuthLinks = !showWeb2Menu;
  const authLinkClass = isDark ? css.connectLink : css.connectLinkLight;

  return (
    <div className={css.root} data-testid="mega-menu-session-controls">
      {showWeb2Menu ? <MegaMenuAccountMenu currentUser={currentUser} onLogout={onLogout} /> : null}
      {showWeb2AuthLinks ? (
        <>
          <NamedLink name="LoginPage" className={authLinkClass} data-testid="mega-menu-sign-in">
            Sign in
          </NamedLink>
          <NamedLink name="SignupPage" className={css.authLinkSecondary} data-testid="mega-menu-sign-up">
            Sign up
          </NamedLink>
        </>
      ) : null}
      {authMode !== 'web2' ? <MegaMenuWalletMenu isDark={isDark} /> : null}
      {showWeb2Menu ? (
        <Link to="/wallet" className={isDark ? css.connectLink : css.connectLinkLight}>
          Link wallet
        </Link>
      ) : null}
    </div>
  );
};

export default MegaMenuSessionControls;
